import { forwardRef, type InputHTMLAttributes } from 'react';

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Si true, renderiza con borde rojo + ring de error. NO inyecta texto de
   * error (eso lo hace FormField). Usado cuando el caller controla el shape
   * "input rojo" independiente del FormField (ej. validación en tiempo real).
   */
  error?: boolean;
  /**
   * Default: 'text'. Acepta los tipos textuales típicos de HTML inputs +
   * 'number' + 'date' (filtros con range). NO acepta checkbox/radio/file —
   * usar los primitives dedicados.
   */
  type?: 'text' | 'email' | 'url' | 'password' | 'number' | 'tel' | 'search' | 'date';
}

/**
 * Input de texto estilizado contra los tokens del módulo. Wrappea el HTML
 * native `<input>` con la clase `.input` legacy (tokens.css línea ~630),
 * agregando estado de error via atributo `data-error` que un CSS rule nuevo
 * pinta con borde + ring rojos.
 *
 * Forward ref para que React Hook Form y similares puedan focusear.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput({ error, type = 'text', className, ...rest }, ref) {
    const classes = ['input'];
    if (className) classes.push(className);
    return (
      <input
        ref={ref}
        type={type}
        className={classes.join(' ')}
        data-error={error ? 'true' : undefined}
        {...rest}
      />
    );
  },
);
